import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useFontSize, FontSize } from '../hooks/useFontSize';
import '../styles/pages/guestCollectionView.css';
import '../styles/header.css';
import '../styles/components/unifiedHeader.css';
import '../styles/pages/auctionDetailPage.css';
import API_BASE_URL from '../config';

interface AuctionItem {
  id: number;
  item_id: number;
  seller_id: number;
  auction_group: string | null;
  starts_at: string;
  ends_at: string;
  currency: string;
  minimum_bid: number;
  reserve_price: number | null;
  reserve_visible: boolean;
  buy_now_price: number | null;
  quantity: number;
  status: string;
  outcome: string;
  winner_user_id: number | null;
  winning_bid: number | null;
  collection_window_days: number;
  collection_notes: string | null;
  item_name: string;
  item_description: string | null;
  image_id: number | null;
  seller_name: string;
  seller_location: string | null;
  bid_count: number;
  distance_miles: number | null;
  is_seller: boolean;
  my_bid: Bid | null;
}

interface Bid {
  id: number;
  auction_item_id: number;
  user_id: number;
  amount: number;
  comment: string | null;
  is_active: boolean;
  placed_at: string;
  result: string;
  collection_deadline: string | null;
}

const centsToDisplay = (cents: number) => (cents / 100).toFixed(2);

const formatTimeRemaining = (endsAt: string): string => {
  const now = new Date();
  const end = new Date(endsAt);
  const diff = end.getTime() - now.getTime();

  if (diff <= 0) return 'Ended';

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (days > 0) return `${days}d ${hours}h left`;
  if (hours > 0) return `${hours}h ${minutes}m left`;
  return `${minutes}m left`;
};

const formatDistance = (miles: number | null): string | null => {
  if (miles === null) return null;
  if (miles < 1) return '<1 mi away';
  return `~${Math.round(miles)} mi away`;
};

const AuctionDetailPage: React.FC = () => {
  const { auctionItemId } = useParams<{ auctionItemId: string }>();
  const navigate = useNavigate();
  const { fontSize, setFontSize } = useFontSize();

  const [auction, setAuction] = useState<AuctionItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Bid form state
  const [bidAmount, setBidAmount] = useState('');
  const [bidComment, setBidComment] = useState('');
  const [bidSubmitting, setBidSubmitting] = useState(false);
  const [bidMessage, setBidMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Countdown
  const [timeRemaining, setTimeRemaining] = useState('');

  // Guest theme — default to system preference, not the logged-in user's stored theme
  const [guestTheme, setGuestTheme] = useState<'light' | 'dark'>(() => {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });
  const savedThemePrefRef = React.useRef<string | null>(null);
  useEffect(() => {
    savedThemePrefRef.current = localStorage.getItem('theme-preference');
    localStorage.setItem('theme-preference', 'system');
    const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', systemTheme);
    return () => {
      if (savedThemePrefRef.current !== null) {
        localStorage.setItem('theme-preference', savedThemePrefRef.current);
      } else {
        localStorage.removeItem('theme-preference');
      }
      document.documentElement.setAttribute('data-theme',
        savedThemePrefRef.current === 'dark' ? 'dark'
          : savedThemePrefRef.current === 'light' ? 'light'
          : savedThemePrefRef.current === 'pastel' ? 'pastel'
          : window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
      );
    };
  }, []);
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', guestTheme);
  }, [guestTheme]);

  const token = localStorage.getItem('auth_token');

  const fetchAuction = useCallback(async () => {
    if (!auctionItemId) return;
    try {
      setLoading(true);
      if (!token) {
        setError('Sign in to view auction details');
        return;
      }
      const response = await fetch(`${API_BASE_URL}/api/auction-items/browse/${auctionItemId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        if (response.status === 401) {
          setError('Sign in to view auction details');
        } else {
          throw new Error('Failed to load auction');
        }
        return;
      }
      const data = await response.json();
      setAuction(data);

      // Pre-fill bid amount
      if (data.my_bid) {
        setBidAmount((data.my_bid.amount / 100).toFixed(2));
        setBidComment(data.my_bid.comment || '');
      } else {
        setBidAmount((data.minimum_bid / 100).toFixed(2));
      }
    } catch (err) {
      console.error('Error fetching auction:', err);
      setError('Unable to load auction. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [auctionItemId, token]);

  useEffect(() => {
    fetchAuction();
  }, [fetchAuction]);

  // Countdown timer
  useEffect(() => {
    if (!auction) return;
    const update = () => setTimeRemaining(formatTimeRemaining(auction.ends_at));
    update();
    const interval = setInterval(update, 30000);
    return () => clearInterval(interval);
  }, [auction]);

  const handlePlaceBid = async () => {
    if (!auction || !token) return;
    const amountCents = Math.round(parseFloat(bidAmount) * 100);
    if (isNaN(amountCents) || amountCents < auction.minimum_bid) {
      setBidMessage({ type: 'error', text: `Bid must be at least $${centsToDisplay(auction.minimum_bid)}` });
      return;
    }
    if (bidComment.length > 500) {
      setBidMessage({ type: 'error', text: 'Comment must be 500 characters or fewer' });
      return;
    }

    setBidSubmitting(true);
    setBidMessage(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/auction-items/${auction.id}/bid`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          amount: amountCents,
          comment: bidComment.trim() || null,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        setBidMessage({ type: 'error', text: data.error || 'Failed to place bid' });
        return;
      }
      setBidMessage({ type: 'success', text: `Bid of $${centsToDisplay(amountCents)} placed. You can revise anytime before the auction ends.` });
      fetchAuction();
    } catch {
      setBidMessage({ type: 'error', text: 'Failed to place bid. Please try again.' });
    } finally {
      setBidSubmitting(false);
    }
  };

  const handleWithdrawBid = async () => {
    if (!auction || !token) return;
    if (!window.confirm('Withdraw your bid? You can place a new one later if the auction is still active.')) return;

    setBidSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/auction-items/${auction.id}/bid`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        const data = await response.json();
        setBidMessage({ type: 'error', text: data.error || 'Failed to withdraw bid' });
        return;
      }
      setBidMessage({ type: 'success', text: 'Bid withdrawn.' });
      setBidAmount('');
      setBidComment('');
      fetchAuction();
    } catch {
      setBidMessage({ type: 'error', text: 'Failed to withdraw bid.' });
    } finally {
      setBidSubmitting(false);
    }
  };

  const handleBuyNow = async () => {
    if (!auction || !token) return;
    if (!window.confirm(`Express interest in buying at $${centsToDisplay(auction.buy_now_price!)}? The seller will be notified.`)) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/auction-items/${auction.id}/buy-now`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) {
        setBidMessage({ type: 'error', text: data.error || 'Failed to send Buy It Now request' });
        return;
      }
      setBidMessage({ type: 'success', text: 'Seller has been notified of your interest.' });
    } catch {
      setBidMessage({ type: 'error', text: 'Failed to send request.' });
    }
  };

  const imageUrl = auction?.image_id
    ? `${API_BASE_URL}/api/images/${auction.image_id}/public`
    : null;

  return (
    <div className="guest-collection-view">
      {/* Header */}
      <div
        ref={(el) => {
          if (el) {
            const h = el.getBoundingClientRect().height;
            document.documentElement.style.setProperty('--guest-header-h', `${Math.ceil(h)}px`);
          }
        }}
        style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000, background: 'var(--color-bg-primary)', borderBottom: '1px solid var(--color-border)' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 1rem', position: 'relative' }}>
          <button
            onClick={() => navigate('/marketplace')}
            style={{ background: 'transparent', border: 'none', padding: '8px', cursor: 'pointer', color: 'var(--color-text-primary)', display: 'flex', alignItems: 'center', gap: '4px' }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
            <span style={{ fontSize: '14px' }}>Marketplace</span>
          </button>
          <div style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
            <div className="unified-header-title">Auction</div>
          </div>
          <div className="unified-header-actions">
            <button
              onClick={() => {
                const sizes: FontSize[] = ['small', 'medium', 'large', 'x-large'];
                const currentIndex = sizes.indexOf(fontSize);
                const nextIndex = (currentIndex + 1) % sizes.length;
                setFontSize(sizes[nextIndex]);
              }}
              className="unified-header-font-toggle"
              title={`Font size: ${fontSize}`}
              aria-label="Change font size"
            >
              <span className="font-toggle-text">Aa</span>
              <span className="font-toggle-bars">
                <span className={`bar bar-1 ${['small','medium','large','x-large'].indexOf(fontSize) >= 0 ? 'active' : ''}`}></span>
                <span className={`bar bar-2 ${['medium','large','x-large'].indexOf(fontSize) >= 0 ? 'active' : ''}`}></span>
                <span className={`bar bar-3 ${['large','x-large'].indexOf(fontSize) >= 0 ? 'active' : ''}`}></span>
                <span className={`bar bar-4 ${fontSize === 'x-large' ? 'active' : ''}`}></span>
              </span>
            </button>
            <button
              onClick={() => setGuestTheme(prev => prev === 'light' ? 'dark' : 'light')}
              style={{ background: 'transparent', border: 'none', padding: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-secondary)' }}
              title={`Theme: ${guestTheme}`}
            >
              {guestTheme === 'light' ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="guest-content">
        {loading ? (
          <div className="marketplace-loading">Loading auction...</div>
        ) : error ? (
          <div className="auction-auth-prompt">
            <p>{error}</p>
            <button
              onClick={() => navigate('/help')}
              className="auction-signin-btn"
            >
              Sign In
            </button>
          </div>
        ) : auction ? (
          <div className="auction-detail">
            {/* Item image */}
            <div className="auction-detail-image">
              {imageUrl ? (
                <img src={imageUrl} alt={auction.item_name} />
              ) : (
                <div className="auction-detail-placeholder">
                  {auction.item_name.substring(0, 2).toUpperCase()}
                </div>
              )}
              {/* Status badge */}
              <div className={`auction-status-badge auction-status-${auction.status}`}>
                {auction.status === 'active' ? timeRemaining : auction.status.charAt(0).toUpperCase() + auction.status.slice(1)}
              </div>
            </div>

            {/* Item info */}
            <div className="auction-detail-info">
              <h1 className="auction-detail-title">{auction.item_name}</h1>
              {auction.item_description && (
                <p className="auction-detail-description">{auction.item_description}</p>
              )}

              {auction.quantity > 1 && (
                <div className="auction-detail-qty">Lot of {auction.quantity}</div>
              )}

              {/* Pricing grid */}
              <div className="auction-pricing-grid">
                <div className="auction-pricing-item">
                  <span className="auction-pricing-label">Minimum Bid</span>
                  <span className="auction-pricing-value">${centsToDisplay(auction.minimum_bid)}</span>
                </div>
                {auction.reserve_visible && auction.reserve_price && (
                  <div className="auction-pricing-item">
                    <span className="auction-pricing-label">Reserve Price</span>
                    <span className="auction-pricing-value">${centsToDisplay(auction.reserve_price)}</span>
                  </div>
                )}
                {auction.buy_now_price && (
                  <div className="auction-pricing-item">
                    <span className="auction-pricing-label">Buy It Now</span>
                    <span className="auction-pricing-value auction-buy-now-price">${centsToDisplay(auction.buy_now_price)}</span>
                  </div>
                )}
                <div className="auction-pricing-item">
                  <span className="auction-pricing-label">Bids</span>
                  <span className="auction-pricing-value">{auction.bid_count}</span>
                </div>
              </div>

              {/* Seller info */}
              <div className="auction-seller-info">
                <span className="auction-seller-name">Seller: {auction.seller_name}</span>
                {auction.seller_location && (
                  <span className="auction-seller-distance">{auction.seller_location}</span>
                )}
              </div>

              {auction.collection_notes && (
                <div className="auction-collection-notes">
                  <strong>Pickup:</strong> {auction.collection_notes}
                </div>
              )}

              {/* Auction outcome (if closed/completed) */}
              {auction.status === 'closed' && (
                <div className="auction-outcome-banner auction-outcome-closed">
                  Auction ended — {auction.outcome === 'sold' ? 'Sold' : auction.outcome === 'unsold' ? 'Unsold' : 'Awaiting collection'}
                </div>
              )}
              {auction.status === 'completed' && (
                <div className="auction-outcome-banner auction-outcome-completed">
                  {auction.outcome === 'sold' ? `Sold for $${centsToDisplay(auction.winning_bid || 0)}` : 'Auction completed — unsold'}
                </div>
              )}
              {auction.status === 'cancelled' && (
                <div className="auction-outcome-banner auction-outcome-cancelled">
                  Seller withdrew this item from auction
                </div>
              )}

              {/* Bid section — only when active and not seller */}
              {auction.status === 'active' && !auction.is_seller && (
                <div className="auction-bid-section">
                  <h2 className="auction-bid-title">
                    {auction.my_bid ? 'Your Bid' : 'Place a Bid'}
                  </h2>

                  {auction.my_bid && (
                    <div className="auction-current-bid">
                      Your current bid: <strong>${centsToDisplay(auction.my_bid.amount)}</strong>
                    </div>
                  )}

                  <div className="auction-bid-form">
                    <div className="auction-bid-amount-row">
                      <span className="auction-bid-currency">$</span>
                      <input
                        type="number"
                        className="auction-bid-input"
                        value={bidAmount}
                        onChange={(e) => setBidAmount(e.target.value)}
                        min={(auction.minimum_bid / 100).toFixed(2)}
                        step="0.01"
                        placeholder={(auction.minimum_bid / 100).toFixed(2)}
                        disabled={bidSubmitting}
                      />
                    </div>

                    {auction.reserve_visible && auction.reserve_price && (
                      parseFloat(bidAmount) * 100 < auction.reserve_price && (
                        <div className="auction-bid-warning">
                          Your bid is below the reserve price of ${centsToDisplay(auction.reserve_price)}
                        </div>
                      )
                    )}

                    <div className="auction-bid-actions">
                      <button
                        className="auction-bid-submit"
                        onClick={handlePlaceBid}
                        disabled={bidSubmitting}
                      >
                        {bidSubmitting ? 'Submitting...' : auction.my_bid ? 'Update Bid' : 'Place Bid'}
                      </button>
                      {auction.my_bid && (
                        <button
                          className="auction-bid-withdraw"
                          onClick={handleWithdrawBid}
                          disabled={bidSubmitting}
                        >
                          Withdraw
                        </button>
                      )}
                    </div>
                  </div>

                  {bidMessage && (
                    <div className={`auction-bid-message auction-bid-message-${bidMessage.type}`}>
                      {bidMessage.text}
                    </div>
                  )}
                </div>
              )}

              {/* Buy It Now button */}
              {auction.status === 'active' && auction.buy_now_price && !auction.is_seller && (
                <button
                  className="auction-buy-now-btn"
                  onClick={handleBuyNow}
                >
                  Buy It Now — ${centsToDisplay(auction.buy_now_price)}
                </button>
              )}

              {/* Seller view note */}
              {auction.is_seller && auction.status === 'active' && (
                <div className="auction-seller-note">
                  This is your auction. You can manage it from your inventory.
                </div>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default AuctionDetailPage;
